import { Box, Theme, createStyles, makeStyles } from '@material-ui/core';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';
import { fetchTwoWaySmsCountThunk, searchTwoWaySmsThunk } from 'actions/twoWaySms/twoWaySmsActions';
import { SmsChat } from 'model/TwoWaySms';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MessageFilterTypes } from '../Model';
import SearchChatsResultItem from './SearchChatsResultItem';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { wbp, reduceResolution } from 'Theme';
import { TwoWaySmsStatus } from 'reducers/twowaysms/TwoWaySmsState';
import Loader from '../ui/Loader';
import { Meta } from 'model/ProspectMessage';

interface SearchChatsResultProps {
    chatSearchText: string;
    selectedFilter: string;
    marketplaceId: number;
    clientSelected?: number;
    onSelectClient?: (selectedClientId: number | undefined) => void;
    onClearSelectedClient?: () => void;
}
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        chatsContainer: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
        },
        chatContainer: {
            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(25)}px`,
                marginBottom: `${reduceResolution(10)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '25px',
                marginBottom: '10px'
            },

            '&:hover': {
                backgroundColor: '#F8F5F1'
            }
        },
        chatScroll: {
            flex: 1,
            overflowY: 'scroll',
            overflowX: 'hidden',
            padding: `${reduceResolution(15)}px ${reduceResolution(15)}px ${reduceResolution(15)}px ${reduceResolution(
                15
            )}px`
        },
        noMorePages: {
            color: '#7A7A7A',
            margin: '37px 0',
            textAlign: 'center'
        }
    })
);

const defaultPageSize = 30;

const SearchChatsResult: FunctionComponent<SearchChatsResultProps> = props => {
    const chatScrollRef = useRef<OverlayScrollbarsComponent>(null);
    const lastVisibleChatRefId = useRef<string | null>(null);

    const classes = useStyles();
    const dispatch = useDispatch();

    const chats = useSelector<RootState, Array<SmsChat>>(state => state.twoWaySms.chats);
    const meta = useSelector<RootState, Meta | null>(state => state.twoWaySms.metaConversations);

    const twoWaySmsStatus = useSelector<RootState, TwoWaySmsStatus>(state => state.twoWaySms.status);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(defaultPageSize);
    const [loading, setLoading] = useState<boolean>(false);
    const [scrollAvailable, setScrollAvailable] = useState(false);

    const hasMorePages = useMemo(() => {
        if (!meta) return false;
        return meta.page < meta.totalPages;
    }, [meta]);

    const fetchChats = (pageNumber: number) => {
        setLoading(true);
        if (props.chatSearchText.length === 0) {
            dispatch(fetchTwoWaySmsCountThunk(props.marketplaceId));
            dispatch(
                searchTwoWaySmsThunk(
                    '',
                    pageNumber,
                    pageSize,
                    props.selectedFilter === MessageFilterTypes.UNREAD ? 'unread' : 'all',
                    props.selectedFilter,
                    props.marketplaceId
                )
            );
        } else if (props.chatSearchText.length >= 3) {
            dispatch(
                searchTwoWaySmsThunk(
                    props.chatSearchText,
                    1,
                    pageSize,
                    'all',
                    props.selectedFilter,
                    props.marketplaceId
                )
            );
        }
    };

    useEffect(() => {
        fetchChats(page);
    }, [page]);

    useNonInitialEffect(() => {
        setPage(1);
        setLoading(true);

        if (props.chatSearchText.length === 0) {
            dispatch(fetchTwoWaySmsCountThunk(props.marketplaceId));
        }

        switch (props.selectedFilter) {
            case MessageFilterTypes.NEWEST:
                dispatch(
                    searchTwoWaySmsThunk(
                        props.chatSearchText,
                        1,
                        pageSize,
                        'all',
                        MessageFilterTypes.NEWEST,
                        props.marketplaceId
                    )
                );
                break;
            case MessageFilterTypes.OLDEST:
                dispatch(
                    searchTwoWaySmsThunk(
                        props.chatSearchText,
                        1,
                        pageSize,
                        'all',
                        MessageFilterTypes.OLDEST,
                        props.marketplaceId
                    )
                );
                break;
            case MessageFilterTypes.UNREAD:
                dispatch(
                    searchTwoWaySmsThunk(
                        props.chatSearchText,
                        1,
                        pageSize,
                        'unread',
                        MessageFilterTypes.UNREAD,
                        props.marketplaceId
                    )
                );
                break;
            default:
                break;
        }
        props.onClearSelectedClient && props.onClearSelectedClient();
    }, [props.selectedFilter, props.chatSearchText, props.marketplaceId]);

    useNonInitialEffect(() => {
        if (twoWaySmsStatus === TwoWaySmsStatus.SearchSuccess || twoWaySmsStatus === TwoWaySmsStatus.FetchSuccess) {
            setLoading(false);

            if (page > 1) {
                setTimeout(() => {
                    if (lastVisibleChatRefId.current) {
                        const el = document.getElementById(lastVisibleChatRefId.current);
                        el?.scrollIntoView({ block: 'start', behavior: 'auto' });
                    }
                }, 100);
            }
        }
    }, [twoWaySmsStatus]);

    const handleChatScroll = () => {
        if (chatScrollRef.current) {
            const instance = chatScrollRef.current.osInstance();
            const vp = instance?.getElements().viewport;
            if (vp) {
                const { scrollTop, scrollHeight, clientHeight } = vp;
                setScrollAvailable(scrollHeight > clientHeight);
                const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

                const chatElements = vp.querySelectorAll('[id^="chat-"]');

                for (const element of Array.from(chatElements)) {
                    const rect = (element as HTMLElement).getBoundingClientRect();
                    const vpRect = vp.getBoundingClientRect();

                    if (rect.top >= vpRect.top && rect.bottom <= vpRect.bottom) {
                        lastVisibleChatRefId.current = element.id;
                    }
                }

                if (distanceFromBottom < 5 && hasMorePages && !loading) {
                    setPage(prev => prev + 1);
                }
            }
        }
    };

    return (
        <OverlayScrollbarsComponent
            className={classes.chatScroll}
            options={{
                callbacks: {
                    onScrollStop: handleChatScroll
                }
            }}
            ref={chatScrollRef}
        >
            {chats && (
                <Box
                    id="chatsContainer"
                    className={classes.chatsContainer}
                >
                    {loading && <Loader />}
                    {!loading &&
                        chats.map(chat => {
                            return (
                                <Box key={chat.message.uuid + '-' + chat.client.id + '-' + '-root'}>
                                    <SearchChatsResultItem
                                        chat={chat}
                                        active={chat.client.id === props.clientSelected}
                                        onClick={() => {
                                            props.onSelectClient && props.onSelectClient(chat.client.id);
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    {meta &&
                        meta.page === meta.totalPages &&
                        chats.length > 0 &&
                        !(props.chatSearchText.length >= 3) &&
                        scrollAvailable && <Box className={classes.noMorePages}>{"🎉 You're all caught up."}</Box>}
                </Box>
            )}
        </OverlayScrollbarsComponent>
    );
};

export default SearchChatsResult;
